/**
 * Ops Router
 *
 * Routes for ops.claimshieldpro.com
 * Role-based access for staff and admin users.
 */
import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { OpsLayout, LoginView, ForgotPasswordView, ResetPasswordView, NotFoundView, setupGuards } from '@claimshield/shared'
import { useAuthStore } from '@shared/stores/auth'
import type { User } from '@shared/types/auth'
import { isChunkLoadErrorMessage, attemptChunkReload } from '@claimshield/shared/router/chunk-reload'
// The legal department's ten routes, in one module so that no screen worker
// ever edits this 1,500-line file. See src/router/legal.ts for the auth story
// (LEGAL_ROUTE_ROLES mirrors the backend's CurrentLegal verbatim).
import { legalRoutes } from './legal'

declare module 'vue-router' { interface RouteMeta { requiresAuditDesk?: boolean } }

// Exported so a spec can drive the SAME records through a memory-history
// router without the guards this module installs below: `router.resolve()`
// deliberately does not follow a `redirect:`, so the only honest proof that an
// old path still lands is a real navigation — see tests/unit/atlasRouteFamily.spec.ts.
export const routes: RouteRecordRaw[] = [
  // Auth routes (public)
  {
    path: '/login',
    name: 'login',
    component: LoginView,
    meta: { requiresAuth: false, layout: 'auth', title: 'Sign In | ClaimShield Ops' },
  },
  {
    path: '/forgot-password',
    name: 'forgot-password',
    component: ForgotPasswordView,
    meta: { requiresAuth: false, layout: 'auth', title: 'Forgot Password | ClaimShield Ops' },
  },
  {
    path: '/reset-password',
    name: 'reset-password',
    component: ResetPasswordView,
    meta: { requiresAuth: false, layout: 'auth', title: 'Reset Password | ClaimShield Ops' },
  },

  // Ops routes (authenticated staff/admin)
  {
    path: '/',
    component: OpsLayout,
    meta: { requiresAuth: true, roles: ['staff', 'admin'] },
    children: [
      // Dashboard — all users see the same ops dashboard
      {
        path: '',
        name: 'dashboard',
        component: () => import('@views/DashboardView.vue'),
        meta: { title: 'Dashboard | ClaimShield Ops' },
      },
      {
        path: 'ops-dashboard',
        name: 'admin-dashboard',
        redirect: { name: 'dashboard' },
      },
      // Per-client migration file review (portal-launch P2.1) — deep-linked
      // from the rollout worklist row; the D5 gate's working surface.
      {
        path: 'portal-rollout/:clientId/file-review',
        name: 'migration-file-review',
        component: () => import('@views/MigrationReviewView.vue'),
        // `developer` admitted for the accounts named in Sentinel's
        // MIGRATION_REVIEWERS (Ahmed, 2026-10-26: Abdullah and Nauman review the
        // migration exceptions). The server decides who is named; any other
        // developer gets 403 from every call this page makes.
        meta: { title: 'Migration file review | ClaimShield Ops', roles: ['staff', 'admin', 'developer'] },
      },
      // The WorkDrive->S3 migration's waves: status, gates, batches, the
      // accuracy sample, and start / pause (Ahmed, 2026-10-28: Nauman co-leads
      // the migration). `developer` admitted for the accounts named in
      // Sentinel's MIGRATION_LEADS; the server decides who is named, and any
      // other developer gets 403 from every call this page makes. No `staff`:
      // starting a wave spends against the cap, which consultants do not.
      {
        path: 'migration-waves',
        name: 'migration-waves',
        component: () => import('@views/MigrationWavesView.vue'),
        meta: { title: 'Migration waves | ClaimShield Ops', roles: ['admin', 'developer'] },
      },
      {
        path: 'my-dashboard',
        name: 'consultant-dashboard',
        redirect: { name: 'dashboard' },
      },

      // Action Center — Dugger's cross-source attention ledger (email/Cliq/
      // system finds in one triageable queue). NOT the Robley Action Center
      // (robley/action-center below). ADMIN ONLY (Gavin + Dugger) per spec
      // (knowledge/action-center/specs/2026-07-09-action-center-design.md:101)
      // — the ledger surfaces body_excerpt from personal email/DM content and
      // grants mutation authority, so it overrides the root block's
      // staff+admin default rather than inheriting it.
      // API: /api/v1/action-center · docs: knowledge/action-center/
      {
        path: 'action-center',
        name: 'action-center',
        component: () => import('@views/action-center/ActionCenterView.vue'),
        meta: { title: 'Action Center | ClaimShield Ops', roles: ['admin'] },
      },
      {
        path: 'action-center-coverage',
        name: 'action-center-coverage',
        component: () => import('@views/ActionCenterCoverageView.vue'),
        meta: { title: 'Action Center Coverage | ClaimShield Ops', roles: ['staff', 'admin'] },
      },
      {
        path: 'audit-desk',
        name: 'audit-desk',
        component: () => import('@views/audit-center/AuditDeskView.vue'),
        meta: { title: 'Audit Desk | ClaimShield Ops', roles: ['staff', 'admin'], requiresAuditDesk: true },
      },
      // The paid door (W2, 2026-11-03): desk-only, like the desk itself. Static segments outrank
      // 'audit-desk/:id', so 'prospects' never resolves as an event id.
      {
        path: 'audit-desk/prospects',
        name: 'audit-desk-prospects',
        component: () => import('@views/audit-center/ProspectListView.vue'),
        meta: { title: 'Paid Door | ClaimShield Ops', roles: ['staff', 'admin'], requiresAuditDesk: true },
      },
      {
        path: 'audit-desk/prospects/new',
        name: 'audit-desk-prospect-intake',
        component: () => import('@views/audit-center/ProspectIntakeView.vue'),
        meta: { title: 'Add a Carrier | ClaimShield Ops', roles: ['staff', 'admin'], requiresAuditDesk: true },
      },
      {
        path: 'audit-desk/prospects/:id',
        name: 'audit-desk-prospect',
        component: () => import('@views/audit-center/ProspectDetailView.vue'),
        meta: { title: 'Paid Door Carrier | ClaimShield Ops', roles: ['staff', 'admin'], requiresAuditDesk: true },
      },
      {
        path: 'audit-desk/new',
        name: 'audit-desk-new',
        component: () => import('@views/audit-center/OpenEventView.vue'),
        meta: { title: 'Open Audit | ClaimShield Ops', roles: ['staff', 'admin'] },
      },
      {
        path: 'audit-desk/:id',
        name: 'audit-desk-detail',
        component: () => import('@views/audit-center/EventDetailView.vue'),
        meta: { title: 'Audit Event | ClaimShield Ops', roles: ['staff', 'admin'] },
      },
      {
        path: 'audit-readiness/worst',
        name: 'audit-readiness-worst',
        component: () => import('@views/AuditReadinessWorstView.vue'),
        meta: { title: 'Audit Readiness | ClaimShield Ops', roles: ['staff', 'admin'] },
      },

      // Book — one consultant's whole book of open driver items, worked one
      // driver at a time. Opened to staff 2026-10-01 (Dugger): the Book is a
      // different VIEW of data consultants already reach through the ops
      // dashboard, so it shows them nothing new, and a consultant landing on
      // the largest book instead of their own is worse than the wider scope.
      // API: /api/v1/consultant-book (staff-readable, read-only) — this guard
      // was the only thing holding the page back, so widening the route does
      // not widen any data beyond what authorized_client_ids already grants.
      {
        path: 'book',
        name: 'consultant-book',
        component: () => import('@views/book/BookView.vue'),
        meta: { title: 'Book | ClaimShield Ops', roles: ['staff', 'admin'] },
      },
      {
        path: 'portal-access-requests',
        name: 'portal-access-requests',
        component: () => import('@views/PortalAccessRequestsView.vue'),
        meta: { title: 'Portal Access Requests | ClaimShield Ops', roles: ['staff', 'admin'] },
      },

      // Clearinghouse audit (staff + admin)
      {
        path: 'clearinghouse/audit',
        name: 'clearinghouse-audit',
        component: () => import('@views/admin/clearinghouse/AuditOverviewView.vue'),
        meta: { title: 'Clearinghouse Audit | ClaimShield Ops' },
      },
      {
        path: 'clearinghouse/audit/:consultant',
        name: 'clearinghouse-audit-consultant',
        component: () => import('@views/admin/clearinghouse/AuditConsultantView.vue'),
        meta: { title: 'Consultant Audit | ClaimShield Ops' },
      },
      // Rule survey — consultants confirm/correct the logic we apply to each
      // FMCSA result. Staff-visible (they are the respondents); the aggregate
      // results view is admin-gated at the API.
      {
        path: 'clearinghouse/rule-survey',
        name: 'clearinghouse-rule-survey',
        component: () => import('@views/admin/clearinghouse/RuleSurveyView.vue'),
        meta: { title: 'Clearinghouse Rule Check | ClaimShield Ops' },
      },

      // Consultant workflow review — the consultants check the drafted SOP
      // against the job they actually do; the answers become SOP v1.
      // Staff-visible (they are the respondents); the aggregate results view
      // is admin-gated at the API.
      {
        path: 'consultant-workflow/review',
        name: 'consultant-workflow-review',
        component: () => import('@views/ConsultantWorkflowReviewView.vue'),
        meta: { title: 'How the job works | ClaimShield Ops' },
      },

      // Insurance submissions (Robley)
      {
        path: 'insurance/submissions',
        name: 'insurance-submissions',
        component: () => import('@views/insurance/InsuranceSubmissionsView.vue'),
        meta: { title: 'Insurance Submissions | ClaimShield Ops' },
      },
      {
        // Producer "page 1" — DOT lookup, contact info, Robley WorkDrive
        // folder picker. Provisions the folder + the {YYYY} Quoting tree,
        // then hands off to the 8-step intake form. MUST be registered
        // before /:token below so 'new' isn't captured as a token.
        path: 'insurance/submissions/new',
        name: 'insurance-submission-new',
        component: () => import('@views/insurance/StartSubmissionView.vue'),
        meta: { title: 'New Insurance Submission | ClaimShield Ops' },
      },
      {
        path: 'insurance/submissions/:token',
        name: 'insurance-submission-detail',
        component: () => import('@views/insurance/InsuranceSubmissionDetailView.vue'),
        meta: { title: 'Submission Detail | ClaimShield Ops' },
      },
      {
        path: 'insurance/submissions/:token/generate-packets',
        name: 'insurance-generate-packets',
        component: () => import('@views/insurance/GeneratePacketsView.vue'),
        meta: { title: 'Generate Packets | ClaimShield Ops' },
      },
      {
        // Submission Cockpit — per-carrier assemble → present → route → send.
        // Spec: knowledge/robley-integration/specs/2026-05-28-subsystem-c-submission-cockpit-design.md
        path: 'insurance/submissions/:token/cockpit',
        name: 'insurance-submission-cockpit',
        component: () => import('@views/insurance/SubmissionCockpitView.vue'),
        meta: { title: 'Submission Cockpit | ClaimShield Ops' },
      },

      // Robley Prospect Search moved to /prospects in the Atlas route family
      // (wave 4, merge §3.5). The literal stays registered HERE, ahead of the
      // parametric insurance/:token route below, so a stored link is still
      // matched as itself rather than captured as a token — it is now a
      // permanent redirect (N-7) and no longer carries the route name.
      // Spec: knowledge/robley-integration/specs/2026-05-28-subsystem-a-prospecting-nurture-design.md
      {
        path: 'insurance/robley-prospect-search',
        redirect: (to) => ({ name: 'robley-prospect-search', query: to.query, hash: to.hash }),
      },

      // Carrier Hub — /insurance/carriers list + tabbed detail
      // Spec: knowledge/insurance-submission/2026-05-28-carrier-hub-design.md
      {
        path: 'insurance/carriers',
        name: 'insurance-carriers-list',
        component: () => import('@views/insurance/CarriersListView.vue'),
        meta: { title: 'Carriers | ClaimShield Ops' },
      },
      {
        // Engine Pull List — carriers blocked on a bound quote from Robley.
        // Must be registered BEFORE /:carrierId so 'engine-pull-list' is not
        // captured as a carrierId parameter.
        path: 'insurance/engine-pull-list',
        name: 'engine-pull-list',
        component: () => import('@views/insurance/EnginePullListView.vue'),
        meta: { title: 'Engine Pull List | ClaimShield Ops' },
      },
      {
        path: 'insurance/carriers/:carrierId',
        name: 'insurance-carrier-detail',
        component: () => import('@views/insurance/CarrierDetailView.vue'),
        meta: { title: 'Carrier | ClaimShield Ops' },
      },

      // v2 prefilled wizard — staff mirror of marketing's /insurance/:token/v2.
      // Parallel-mounted with the legacy InsuranceIntakeFormView (path below)
      // until v2 reaches feature parity. Uses :token (access_token slug) to
      // match every other ops insurance route; loads submission via GET /{token}
      // then calls POST /{submission.id}/prefill against the UUID.
      {
        path: 'insurance/:token/v2',
        name: 'insurance-form-v2',
        component: () => import('@views/insurance/InsuranceIntakeWizardV2Ops.vue'),
        meta: { title: 'Insurance Wizard v2 | ClaimShield Ops' },
      },

      // Loss-run OCR extraction review (Robley)
      {
        path: 'loss-run-extractions',
        name: 'loss-run-extractions-list',
        component: () => import('@views/loss-run/LossRunExtractionsView.vue'),
        meta: { title: 'Loss Run Review | ClaimShield Ops' },
      },
      {
        path: 'loss-run-extractions/:id',
        name: 'loss-run-extraction-detail',
        component: () => import('@views/loss-run/LossRunExtractionDetailView.vue'),
        meta: { title: 'Loss Run Review | ClaimShield Ops' },
      },
      {
        // Audit file-action deep-link from the compliance audit matrix.
        // Opens View / Replace / Reclassify / Delete for one
        // driver_files row.
        path: 'audit/file/:driverFileId',
        name: 'audit-file-action',
        component: () => import('@views/audit/FileActionView.vue'),
        meta: { title: 'File Actions | ClaimShield Ops' },
      },
      {
        // Orphan WD file deep-link from the audit's folder/PG drift panel.
        // No driver_files row exists yet — actions are Adopt / Trash.
        path: 'audit/orphan/:driverId/:workdriveFileId',
        name: 'audit-orphan-action',
        component: () => import('@views/audit/OrphanFileActionView.vue'),
        meta: { title: 'Orphan File | ClaimShield Ops' },
      },
      {
        // Interactive compliance audit view — replaces the static HTML.
        // Bucket selectable via ?bucket=driver_onboarding / etc.
        path: 'clients/:clientId/audit',
        name: 'client-audit',
        component: () => import('@views/audit/AuditView.vue'),
        meta: { title: 'Compliance Audit | ClaimShield Ops' },
      },
      {
        path: 'insurance/submissions/:token/edit',
        name: 'insurance-submission-edit',
        component: () => import('@views/insurance/InsuranceIntakeFormView.vue'),
        meta: { title: 'Edit Submission | ClaimShield Ops' },
      },
      {
        path: 'insurance/:token',
        name: 'insurance-form',
        component: () => import('@views/insurance/InsuranceIntakeFormView.vue'),
        meta: { title: 'Insurance Form | ClaimShield Ops' },
      },

      // BI dashboards (staff + admin)
      {
        path: 'bi/applications',
        name: 'bi-applications',
        component: () => import('@views/admin/bi/BIApplicationsView.vue'),
        meta: { title: 'Application Dashboard | ClaimShield Ops' },
      },

      // Admin section
      {
        path: 'admin',
        meta: { roles: ['admin'] },
        children: [
          {
            path: '',
            name: 'admin-home',
            component: () => import('@views/admin/AdminDashboardView.vue'),
            meta: { title: 'Admin | ClaimShield Ops' },
          },
          {
            path: 'users',
            name: 'admin-users',
            component: () => import('@views/admin/UsersView.vue'),
            meta: { title: 'Users | ClaimShield Ops' },
          },
          {
            path: 'service-catalog',
            name: 'admin-service-catalog',
            component: () => import('@views/admin/ServiceCatalogView.vue'),
            meta: { title: 'Service Catalog | ClaimShield Ops' },
          },
          {
            // Legacy route — bookmark-safety redirect to the new Carrier Hub.
            // Per 2026-05-28 carrier-hub-design, the Carriers page moved from
            // /admin/carrier-emails to /insurance/carriers.
            path: 'carrier-emails',
            name: 'admin-carrier-emails',
            redirect: { name: 'insurance-carriers-list' },
          },
          {
            path: 'features',
            name: 'admin-features',
            component: () => import('@views/admin/FeaturesView.vue'),
            meta: { title: 'Feature Flags | ClaimShield Ops' },
          },
          {
            path: 'logs',
            name: 'admin-logs',
            component: () => import('@views/admin/LogsView.vue'),
            meta: { title: 'System Logs | ClaimShield Ops' },
          },
          {
            path: 'webhooks',
            name: 'admin-webhooks',
            component: () => import('@views/admin/WebhooksView.vue'),
            meta: { title: 'Webhooks | ClaimShield Ops' },
          },
          {
            // Scheduled-job (Celery beat) liveness board — durable last-success
            // per job. API: GET /api/v1/portal/scheduled-jobs/liveness (admin).
            path: 'scheduled-jobs',
            name: 'admin-scheduled-jobs',
            component: () => import('@views/monitoring/ScheduledJobsView.vue'),
            meta: { title: 'Scheduled Jobs | ClaimShield Ops' },
          },
          {
            // CRM rule activity — what the driver-record listener did, per driver,
            // and the one control that stops it (crm-retirement U1/U4).
            // API: GET/PUT /api/v1/ops/crm-rule-activity (staff).
            //
            // STAFF, overriding this section's admin-only default (Ahmed,
            // 2026-09-11). The audience is the consultant who reports "my status
            // change did nothing" — runbook §3a triages exactly that report from
            // this screen, so gating it to admin left the page shut to the people
            // whose question it answers. The API has always been CurrentStaff;
            // this is the middle of the three layers that must agree (the third
            // is the nav entry in shared/useOpsNav.ts — without it the page is
            // reachable only by typing the URL, which is how it shipped).
            path: 'crm-rule-activity',
            name: 'admin-crm-rule-activity',
            component: () => import('@views/monitoring/CrmRuleActivityView.vue'),
            meta: { title: 'CRM Rule Activity | ClaimShield Ops', roles: ['admin', 'staff'] },
          },
          {
            path: 'fleet/stickers',
            name: 'admin-fleet-stickers',
            component: () => import('@views/FleetStickerInventoryView.vue'),
            meta: { title: 'Fleet Sticker Inventory | ClaimShield Ops' },
          },
          {
            path: 'audit-sweep',
            name: 'admin-audit-sweep',
            component: () => import('@views/admin/AuditSweepView.vue'),
            meta: { title: 'Audit Sweep | ClaimShield Ops' },
          },
          {
            // QA over what the ops assistant tells consultants. ADMIN only —
            // the roles gate is on the parent 'admin' route, and every backing
            // route is CurrentAdmin (app/api/v1/chat_review.py).
            path: 'chat-review',
            name: 'admin-chat-review',
            component: () => import('@views/admin/ChatReviewView.vue'),
            meta: { title: 'Chat Review | ClaimShield Ops' },
          },
          {
            path: 'crm',
            name: 'admin-crm',
            component: () => import('@views/admin/CrmExplorerView.vue'),
            meta: { title: 'CRM Explorer | ClaimShield Ops' },
          },
          {
            path: 'api-docs',
            name: 'admin-api-docs',
            component: () => import('@views/admin/ApiDocsView.vue'),
            meta: { title: 'API Docs | ClaimShield Ops' },
          },
          {
            path: 'docs',
            name: 'admin-docs',
            component: () => import('@views/admin/DocsView.vue'),
            meta: { title: 'Documentation | ClaimShield Ops' },
          },
          {
            path: 'docs/:path(.*)',
            name: 'admin-docs-page',
            component: () => import('@views/admin/DocsView.vue'),
            meta: { title: 'Documentation | ClaimShield Ops' },
          },
          {
            path: 'compliance-screening',
            name: 'admin-compliance-screening',
            component: () => import('@views/admin/ComplianceScreeningView.vue'),
            meta: { title: 'Compliance Screening | ClaimShield Ops' },
          },
          {
            path: 'analytics',
            name: 'admin-analytics',
            component: () => import('@views/admin/AnalyticsView.vue'),
            meta: { title: 'Usage Analytics | ClaimShield Ops' },
          },
          // Careers (HR pipeline — apply to work AT ClaimShield)
          {
            path: 'careers',
            name: 'admin-careers-postings',
            component: () => import('@views/admin/careers/PostingsView.vue'),
            meta: { title: 'Job Postings | ClaimShield Ops' },
          },
          {
            path: 'careers/applications',
            name: 'admin-careers-applications',
            component: () => import('@views/admin/careers/ApplicationsView.vue'),
            // Hiring team (staff allowlist + admins) — overrides the admin-only
            // parent meta so Kathy can reach the pipeline. API still enforces
            // CurrentHiringTeam; the nav entry is allowlist-gated in OpsLayout.
            meta: { title: 'Applications | ClaimShield Ops', roles: ['staff', 'admin'] },
          },
          {
            path: 'bi',
            name: 'admin-bi',
            component: () => import('@views/admin/bi/BIDashboardView.vue'),
            meta: { title: 'BI Dashboard | ClaimShield Ops' },
          },
          {
            path: 'bi/clearinghouse',
            name: 'admin-bi-clearinghouse',
            component: () => import('@views/admin/bi/BIClearinghouseView.vue'),
            meta: { title: 'Clearinghouse Management | ClaimShield Ops' },
          },
          {
            path: 'bi/profitability',
            name: 'admin-bi-profitability',
            component: () => import('@views/admin/bi/BIProfitabilityView.vue'),
            meta: { title: 'Client Profitability | ClaimShield Ops' },
          },
          {
            path: 'bi/monthly-review',
            name: 'admin-bi-monthly-review',
            component: () => import('@views/admin/bi/BIMonthlyReviewView.vue'),
            meta: { title: 'Monthly Business Review | ClaimShield Ops' },
          },
          {
            path: 'bi/financial-projection',
            name: 'admin-bi-financial-projection',
            component: () => import('@views/admin/bi/BIFinancialProjectionView.vue'),
            meta: { title: 'Financial Projection | ClaimShield Ops' },
          },
          {
            path: 'bi/projects',
            name: 'admin-bi-projects',
            component: () => import('@views/admin/bi/ProjectsView.vue'),
            meta: { title: 'Projects | ClaimShield Ops' },
          },
          {
            path: 'bi/goals',
            name: 'admin-bi-goals',
            component: () => import('@views/admin/bi/GoalsView.vue'),
            meta: { title: 'Goals | ClaimShield Ops' },
          },
          {
            path: 'bi/screening',
            name: 'admin-bi-screening',
            component: () => import('@views/admin/bi/BIScreeningView.vue'),
            meta: { title: 'Screening Services Health | ClaimShield Ops' },
          },
          {
            path: 'bi/screening/:service',
            name: 'admin-bi-screening-detail',
            component: () => import('@views/admin/bi/BIScreeningDetailView.vue'),
            meta: { title: 'Screening Service Detail | ClaimShield Ops' },
          },
          {
            path: 'dcc',
            name: 'admin-dcc',
            component: () => import('@views/admin/dcc/DCCDashboardView.vue'),
            meta: { title: 'DCC Management | ClaimShield Ops' },
          },
          {
            path: 'dcc/invoices/:id',
            name: 'admin-dcc-invoice',
            component: () => import('@views/admin/dcc/DCCInvoiceDetailView.vue'),
            meta: { title: 'Invoice Detail | ClaimShield Ops' },
          },
          // Living Audit System
          {
            path: 'audit',
            name: 'audit-dashboard',
            component: () => import('@views/admin/audit/AuditDashboardView.vue'),
            meta: { title: 'Audit Dashboard | ClaimShield Ops' },
          },
          {
            path: 'audit/findings',
            name: 'audit-findings',
            component: () => import('@views/admin/audit/AuditFindingsView.vue'),
            meta: { title: 'Audit Findings | ClaimShield Ops' },
          },
          {
            path: 'audit/rounds/:id',
            name: 'audit-round-detail',
            component: () => import('@views/admin/audit/AuditRoundDetailView.vue'),
            meta: { title: 'Audit Round | ClaimShield Ops' },
          },
          {
            path: 'financials',
            name: 'admin-financials',
            component: () => import('@views/admin/financials/FinancialsView.vue'),
            meta: { title: 'Financials | ClaimShield Ops' },
            children: [
              {
                path: 'q1-2026-projections',
                name: 'admin-financials-q1-2026',
                component: () => import('@views/admin/financials/Q1ProjectionsView.vue'),
                meta: { title: 'Q1 2026 Projections | ClaimShield Ops' },
              },
            ],
          },
          // DCC Unit Economics
          {
            path: 'financials/dcc-economics',
            name: 'dcc-economics',
            component: () => import('@views/admin/financials/DCCEconomicsView.vue'),
            meta: { title: 'DCC Unit Economics | ClaimShield Ops' },
          },
          // Cost Truth
          {
            path: 'financials/cost-truth',
            name: 'admin-cost-truth',
            component: () => import('@views/admin/financials/CostTruthView.vue'),
            meta: { title: 'Cost Truth | ClaimShield Ops' },
          },
          {
            path: 'financials/dcc-economics/services',
            name: 'dcc-services',
            component: () => import('@views/admin/financials/DCCServicesView.vue'),
            meta: { title: 'Service Breakdown | ClaimShield Ops' },
          },
          {
            path: 'financials/dcc-economics/acquisition',
            name: 'dcc-acquisition',
            component: () => import('@views/admin/financials/DCCAcquisitionView.vue'),
            meta: { title: 'Acquisition Cost | ClaimShield Ops' },
          },
          {
            path: 'financials/dcc-economics/churn',
            name: 'dcc-churn',
            component: () => import('@views/admin/financials/DCCChurnView.vue'),
            meta: { title: 'Churn Analysis | ClaimShield Ops' },
          },
          {
            path: 'financials/dcc-economics/client/:id',
            name: 'dcc-client',
            component: () => import('@views/admin/financials/DCCClientView.vue'),
            meta: { title: 'Client DCC | ClaimShield Ops' },
          },
          {
            path: 'billing/invoice-email',
            name: 'admin-invoice-email',
            component: () => import('@views/admin/billing/InvoiceEmailView.vue'),
            meta: { title: 'Invoice Email | ClaimShield Ops' },
          },
          // Price Adjustments
          {
            path: 'billing/price-adjustments',
            name: 'admin-price-adjustments',
            component: () => import('@views/admin/billing/PriceAdjustmentsView.vue'),
            meta: { title: 'Price Adjustments | ClaimShield Ops' },
          },
        ],
      },

      // Transportation section
      {
        path: 'transportation',
        children: [
          {
            path: '',
            name: 'transportation-home',
            component: () => import('@views/transportation/TransportationDashboardView.vue'),
            meta: { title: 'Transportation | ClaimShield Ops' },
          },
          {
            path: 'clients',
            name: 'transportation-clients',
            component: () => import('@views/transportation/ClientsView.vue'),
            meta: { title: 'Clients | ClaimShield Ops' },
          },
          {
            path: 'clients/:id',
            name: 'transportation-client-detail',
            component: () => import('@views/transportation/ClientDetailView.vue'),
            meta: { title: 'Client Details | ClaimShield Ops' },
          },
          {
            path: 'clients/:id/accident-registry',
            name: 'transportation-accident-registry',
            component: () => import('@views/transportation/AccidentRegistryView.vue'),
            meta: { title: 'Accident Registry | ClaimShield Ops' },
          },
          {
            path: 'clients/:cid/drivers/:did/audit-checklist',
            name: 'transportation-driver-audit-checklist',
            component: () => import('@views/transportation/AuditChecklistView.vue'),
            meta: { title: 'Driver Audit Checklist | ClaimShield Ops' },
          },
          {
            path: 'drivers',
            name: 'transportation-drivers',
            component: () => import('@views/transportation/DriversView.vue'),
            meta: { title: 'Drivers | ClaimShield Ops' },
          },
          {
            path: 'drivers-needing-email',
            name: 'transportation-drivers-needing-email',
            component: () => import('@views/transportation/DriversNeedingEmailView.vue'),
            meta: { title: 'Drivers We Cannot Reach | ClaimShield Ops' },
          },
          {
            path: 'drivers-needing-classification',
            name: 'transportation-drivers-needing-classification',
            component: () => import('@views/transportation/DriversNeedingClassificationView.vue'),
            meta: { title: 'Drivers Needing Classification | ClaimShield Ops' },
          },
          {
            path: 'unbranded-carriers',
            name: 'transportation-unbranded-carriers',
            component: () => import('@views/transportation/UnbrandedCarriersView.vue'),
            meta: { title: 'Unbranded Carriers | ClaimShield Ops' },
          },
          {
            // Was a "coming soon" placeholder reached from three menus and two
            // dashboard links (ops audit 2026-10-02, transportation #16).
            // Applicants are worked on the dashboard; an old bookmark lands there.
            path: 'applications',
            name: 'transportation-applications',
            redirect: '/',
          },
          {
            path: 'applications/prototype',
            name: 'transportation-application-prototype',
            component: () => import('@views/applications/DriverApplicationPrototype.vue'),
            meta: { title: 'Driver Application Prototype | ClaimShield Ops' },
          },
          {
            // Same as `applications`: a placeholder with no workflow behind it.
            path: 'queue',
            name: 'transportation-queue',
            redirect: '/',
          },
          {
            path: 'mvrs',
            name: 'transportation-mvrs',
            component: () => import('@views/transportation/MVRsView.vue'),
            meta: { title: 'MVR Orders | ClaimShield Ops' },
          },
          {
            path: 'psps',
            name: 'transportation-psps',
            component: () => import('@views/transportation/PSPsView.vue'),
            meta: { title: 'PSP Orders | ClaimShield Ops' },
          },
          {
            path: 'voe',
            name: 'transportation-voe',
            component: () => import('@views/transportation/VOEView.vue'),
            meta: { title: 'VOE Queue | ClaimShield Ops' },
          },
          {
            path: 'criminal-checks',
            name: 'transportation-criminal-checks',
            component: () => import('@views/transportation/CriminalChecksView.vue'),
            meta: { title: 'Criminal Checks | ClaimShield Ops' },
          },
          {
            path: 'driver-files',
            name: 'transportation-driver-files',
            component: () => import('@views/transportation/DriverFilesView.vue'),
            meta: { roles: ['staff', 'admin', 'developer'], title: 'Driver Files | ClaimShield Ops' },
          },
          {
            path: 'voe/metrics',
            name: 'transportation-voe-metrics',
            component: () => import('@views/transportation/VOEMetricsView.vue'),
            meta: { title: 'VOE Metrics | ClaimShield Ops' },
          },
        ],
      },

      // Industrial section
      {
        path: 'industrial',
        children: [
          {
            path: '',
            name: 'industrial-home',
            component: () => import('@views/industrial/IndustrialDashboardView.vue'),
            meta: { title: 'Industrial | ClaimShield Ops' },
          },
        ],
      },

      // Sales section (proposals, quotes)
      {
        path: 'sales',
        children: [
          {
            path: 'proposals',
            name: 'sales-proposals',
            component: () => import('@views/atlas/ProposalsView.vue'),
            meta: { title: 'Proposals | ClaimShield Ops' },
          },
          {
            path: 'proposals/new',
            name: 'sales-proposal-create',
            component: () => import('@views/atlas/CreateProposalView.vue'),
            meta: { title: 'New Proposal | ClaimShield Ops' },
          },
          {
            path: 'proposals/:id/edit',
            name: 'sales-proposal-edit',
            component: () => import('@views/atlas/CreateProposalView.vue'),
            props: true,
            meta: { title: 'Edit Proposal | ClaimShield Ops' },
          },
          // Carrier Search moved to /atlas/search (wave 4, merge §3.5).
          // Permanent redirect (N-7) — the name travelled with the screen.
          {
            path: 'carrier-search',
            redirect: (to) => ({ name: 'sales-carrier-search', query: to.query, hash: to.hash }),
          },
          // Opportunity board (crm-retirement slice 2) — the Zoho `Deals`
          // STAGEVIEW replacement. STAFF, ungated here to match: every
          // /v1/sales/opportunities route is CurrentStaff and owner-scoped,
          // and the rep it exists for (Prosser) is staff, not admin. `wide`
          // so the seven open-stage columns fit without scrolling inside the 1800px cap.
          // Nav entry lives in shared useOpsNav.ts and is in BOTH branches;
          // all three layers move together or the fix is invisible.
          {
            path: 'opportunities',
            name: 'sales-opportunities',
            component: () => import('@views/atlas/OpportunityBoardView.vue'),
            meta: { title: 'Opportunities | ClaimShield Ops', wide: true },
          },
          // Opportunity record page (crm-retirement slice 3). Declared AFTER
          // the literal 'opportunities' path — registration order is the
          // contract, and a parametric route declared first would swallow it.
          // Same STAFF tier as the board: every route it calls is CurrentStaff
          // and owner-scoped.
          {
            path: 'opportunities/:id',
            name: 'sales-opportunity-record',
            component: () => import('@views/atlas/OpportunityRecordView.vue'),
            props: true,
            meta: { title: 'Opportunity | ClaimShield Ops' },
          },
          // Deal history (crm-retirement slice 1, plan §6.2) — the searchable
          // archive of every attempt ever made, open or closed: Zoho's
          // "all deals" list view, with one search box (name, DOT or phone)
          // and a filter rail. STAFF, ungated here to match: it calls only
          // GET /v1/sales/opportunities, which is CurrentStaff and
          // owner-scoped, and the rep it exists for (Prosser) is staff, not
          // admin. `wide` because a row carries nine facts and the filter
          // rail sits beside the table. Nav entry lives in shared
          // useOpsNav.ts and is in BOTH branches; all three layers (API
          // guard, this meta, the nav entry) move together or the page is
          // invisible to him — sales-cockpit-three-layers.spec.ts pins it.
          {
            path: 'history',
            name: 'sales-opportunity-history',
            component: () => import('@views/atlas/OpportunityHistoryView.vue'),
            meta: { title: 'History | ClaimShield Ops', wide: true },
          },
          // Prospect call queue (consultant-workbench P3.3) — STAFF, per
          // Ahmed's ruling 2026-08-28: the sales cockpit runs on `staff`, no
          // new role for Prosser. Ungated here because the 8 user-facing
          // /v1/prospect-queue endpoints it consumes are CurrentStaff as of
          // 2026-09-04 (the 9 heavy batch routes stay CurrentAdmin — a
          // salesman must not have the score-all button). Nav entry lives in
          // shared useOpsNav.ts and is now in BOTH branches; all three layers
          // move together or the fix is invisible.
          {
            path: 'call-queue',
            name: 'sales-call-queue',
            component: () => import('@views/atlas/CallQueueView.vue'),
            meta: { title: 'Call Queue | ClaimShield Ops' },
          },
          {
            path: 'conversion',
            name: 'sales-conversion-cockpit',
            component: () => import('@views/atlas/ConversionCockpitView.vue'),
            // wide: full-bleed page container so the 12-column kanban fits on
            // a wide display instead of scrolling inside the 1800px cap.
            meta: { title: 'Conversion Cockpit | ClaimShield Ops', wide: true },
          },
          {
            path: 'conversion-review',
            name: 'sales-conversion-review',
            component: () => import('@views/atlas/ConversionReviewView.vue'),
            meta: { title: 'Contact Review | ClaimShield Ops' },
          },
          // WS-F portfolio dashboard + global vehicle search moved to
          // /atlas/portfolio and /atlas/vehicles (wave 4, merge §3.5).
          // Permanent redirects (N-7); the names travelled with the screens.
          {
            path: 'portfolio',
            redirect: (to) => ({ name: 'sales-portfolio', query: to.query, hash: to.hash }),
          },
          {
            path: 'vehicle-search',
            redirect: (to) => ({ name: 'sales-vehicle-search', query: to.query, hash: to.hash }),
          },
        ],
      },

      // =====================================================================
      // ATLAS ROUTE FAMILY (wave 4, step 4.1 — merge §3.5 "Route family",
      // F1 RULED: adopt /atlas/* now, old paths redirect, names stable)
      //
      // TWO THINGS ARE LOAD-BEARING HERE AND NEITHER IS OBVIOUS:
      //
      // 1. `meta.roles` on EVERY record is the §3.5 Roles column, and that
      //    column must equal the router-level auth the backend enforces —
      //    checked against the wave-2 routers at this HEAD with
      //    `rg -n "APIRouter\(" -A6 src/services/sentinel/app/api/v1/_routers/atlas_*.py
      //     src/services/sentinel/app/api/v1/_routers/prospects.py`:
      //    atlas_{search,report,calculator,portfolio,lists,vehicles,shippers,
      //    status} are STAFF+ADMIN+DEVELOPER; atlas_reports (bulk + scheduled)
      //    and prospects are STAFF+ADMIN. Where the two ever disagree the
      //    BACKEND wins — never widen a role here to make them match.
      //    /prospects withholds `developer` deliberately (D-15 residual: the
      //    prospects router is Staff+Admin until Ahmed opens it).
      //    The guard reads a MERGED meta (child wins per key), so declaring
      //    roles on each record is what overrides the layout route's
      //    ['staff','admin'] and lets a developer in at all.
      //
      // 2. The route NAMES are stable on purpose (coordinator ruling, wave 4).
      //    `carrier-profile`, `sales-carrier-search`, `sales-portfolio`,
      //    `sales-vehicle-search`, `shipper-search`, `robley-prospect-search`
      //    and the rest keep the spellings their callers already use —
      //    RecentCarriersMenu (shared), CarrierResultsTable, PortfolioView,
      //    AtlasHomeView, CallQueuePane, the action-center sidecar. A rename
      //    here is a silent dead link in five components, so the names moved
      //    with the screens and the OLD PATHS became nameless redirects.
      //
      // The old paths below each route are PERMANENT redirects, not a
      // one-release courtesy (N-7): action-center rows, Cliq alert posts and
      // scheduled-report emails carry today's URLs, they live outside this
      // repo, and removing the redirect 404s every one of them for no saving.
      // =====================================================================

      // --- Atlas home -----------------------------------------------------
      // The entry point for the parameterised Atlas tools (carrier report,
      // BASICs Calculator, Parity Audit, Shipper Search), none of which can
      // be linked from a sidebar without a DOT or a name. Unchanged path;
      // it is `/atlas` before and after, so it is owed no redirect.
      {
        path: 'atlas',
        name: 'atlas-home',
        component: () => import('@views/atlas/AtlasHomeView.vue'),
        meta: { roles: ['staff', 'admin', 'developer'], title: 'Atlas | ClaimShield Ops' },
      },

      // --- Carrier search -------------------------------------------------
      {
        path: 'atlas/search',
        name: 'sales-carrier-search',
        component: () => import('@views/atlas/CarrierSearchView.vue'),
        meta: { roles: ['staff', 'admin', 'developer'], title: 'Carrier Search | ClaimShield Ops' },
      },

      // --- Atlas Status ledger (tier-B plan B10b) -------------------------
      // Six read-only panels over the tier-B prod-proven FMCSA pipeline
      // (B1/B2/B4/B4-B5/B7/B9). D-20 answered A (Nauman 2026-09-19): REPATH
      // with roles, do not delete. Until this commit this record declared
      // `path: 'atlas'` — the SAME path as atlas-home above it — so
      // vue-router matched the first record and the ledger was unreachable
      // from anywhere in the app; `/atlas/status` fell through to the 404
      // catch-all. That duplicate is what this line resolves.
      {
        path: 'atlas/status',
        name: 'atlas-status',
        component: () => import('@views/atlas/AtlasStatusView.vue'),
        meta: { roles: ['staff', 'admin', 'developer'], title: 'Atlas Status | ClaimShield Ops' },
      },

      // --- Portfolio ------------------------------------------------------
      {
        path: 'atlas/portfolio',
        name: 'sales-portfolio',
        component: () => import('@views/atlas/PortfolioView.vue'),
        meta: { roles: ['staff', 'admin', 'developer'], title: 'Portfolio | ClaimShield Ops' },
      },

      // --- Watchlists (Atlas "lists") -------------------------------------
      {
        path: 'atlas/lists',
        name: 'watchlists',
        component: () => import('@views/atlas/WatchlistsView.vue'),
        meta: { roles: ['staff', 'admin', 'developer'], title: 'Watchlists | ClaimShield Ops' },
      },
      // The per-list screen (§3.5 `/atlas/lists/:id`, wave 5 row 5.5). The
      // read it renders already existed (`GET /atlas/lists/{id}`); the roles
      // are `_routers/atlas_lists.py`'s StaffOrDeveloper set.
      {
        path: 'atlas/lists/:id',
        name: 'atlas-list',
        component: () => import('@views/atlas/ListView.vue'),
        props: true,
        meta: { roles: ['staff', 'admin', 'developer'], title: 'Watchlist | ClaimShield Ops' },
      },

      // --- VITAL: vehicle search + per-VIN history ------------------------
      // Declared literal-first; `atlas/vehicles/:vin` is a longer path and
      // cannot swallow the bare search, but the ordering states the intent.
      {
        path: 'atlas/vehicles',
        name: 'sales-vehicle-search',
        component: () => import('@views/atlas/VehicleSearchView.vue'),
        meta: { roles: ['staff', 'admin', 'developer'], title: 'Vehicle Search | ClaimShield Ops' },
      },
      {
        path: 'atlas/vehicles/:vin',
        name: 'vehicle-history',
        component: () => import('@views/atlas/VehicleHistoryView.vue'),
        props: true,
        meta: { roles: ['staff', 'admin', 'developer'], title: 'Vehicle History | ClaimShield Ops' },
      },

      // --- Shipper search -------------------------------------------------
      {
        path: 'atlas/shippers/:name',
        name: 'shipper-search',
        component: () => import('@views/atlas/ShipperSearchView.vue'),
        props: true,
        meta: { roles: ['staff', 'admin', 'developer'], title: 'Shipper Search | ClaimShield Ops' },
      },

      // --- Bulk + scheduled reports ---------------------------------------
      // STAFF+ADMIN only, matching `_routers/atlas_reports.py`'s
      // require_roles(STAFF, ADMIN) — a developer is NOT admitted here, and
      // that is the backend's call, not a frontend preference. F4: the two
      // screens are deliberately NOT merged.
      {
        path: 'atlas/bulk-reports',
        name: 'bulk-reports',
        component: () => import('@views/atlas/BulkReportsView.vue'),
        meta: { roles: ['staff', 'admin'], title: 'Bulk Reports | ClaimShield Ops' },
      },
      {
        path: 'atlas/scheduled-reports',
        name: 'scheduled-reports',
        component: () => import('@views/atlas/ScheduledReportsView.vue'),
        meta: { roles: ['staff', 'admin'], title: 'Scheduled Reports | ClaimShield Ops' },
      },

      // --- Carrier report + its two per-DOT tools --------------------------
      // `carrier-profile` is the most-linked name in the app (7 push/`:to`
      // sites across ops + shared) — the name does not move, ever.
      {
        path: 'atlas/carriers/:dot',
        name: 'carrier-profile',
        component: () => import('@views/atlas/CarrierProfileView.vue'),
        props: true,
        // Carrier-level FMCSA data keyed by DOT — the builders touch no
        // clients/employees table, so there is no client_scope obligation
        // here. Backed by `_routers/atlas_report.py` (StaffOrDeveloper).
        meta: { roles: ['staff', 'admin', 'developer'], title: 'Carrier Profile | ClaimShield Ops' },
      },
      {
        path: 'atlas/carriers/:dot/calculator',
        name: 'carrier-calculator',
        component: () => import('@views/atlas/CalculatorView.vue'),
        props: true,
        meta: { roles: ['staff', 'admin', 'developer'], title: 'BASICs Calculator | ClaimShield Ops' },
      },
      {
        // §3.5 spells this segment `parity`; the old path was `parity-audit`
        // and redirects below. A3's own screen; `force_fresh` stays an admin
        // inline control on the backend (D-1), not a route-level gate.
        path: 'atlas/carriers/:dot/parity',
        name: 'carrier-parity-audit',
        component: () => import('@views/atlas/ParityAuditView.vue'),
        props: true,
        meta: { roles: ['staff', 'admin', 'developer'], title: 'Parity Audit | ClaimShield Ops' },
      },

      // --- Prospect search (SALEs), rail-led -------------------------------
      // STAFF+ADMIN, matching `_routers/prospects.py`'s
      // require_roles(STAFF, ADMIN). `developer` is WITHHELD on purpose
      // (D-15 residual, coordinator ruling wave 4) — do not add it here
      // before the backend opens it.
      {
        path: 'prospects',
        name: 'robley-prospect-search',
        component: () => import('@views/atlas/RobleyProspectSearchView.vue'),
        meta: { roles: ['staff', 'admin'], title: 'Robley Prospect Search | ClaimShield Ops' },
      },

      // --- Saved Carriers --------------------------------------------------
      // Browser-local star + note list (tier-A plan A8), deliberately
      // distinct from the PG-backed Watchlists above and from the sidecar's
      // "Workspace" nav-group label. NOT one of the §3.5 twelve: F5 retires
      // it into the default watchlist in wave 5, so its path is untouched
      // here and it is owed no redirect.
      {
        path: 'saved-carriers',
        name: 'saved-carriers',
        component: () => import('@views/atlas/SavedCarriersView.vue'),
        // developer: atlas build surface (Ahmed, 2026-09-14). Browser-local
        // star/note list — no API, no client scope to consult.
        meta: { roles: ['staff', 'admin', 'developer'], title: 'Saved Carriers | ClaimShield Ops' },
      },

      // =====================================================================
      // PERMANENT old-path redirects (N-7). Nameless by construction: every
      // name above is unique to the screen it names, and a redirect that
      // carried the name would shadow it. Each forwards params, query AND
      // hash explicitly — vue-router does not promise to carry a query
      // across a redirect, and `?tab=`, `?as_of=`, `?vin=` and `?embed=1`
      // are all live on these URLs.
      // =====================================================================
      {
        path: 'watchlists',
        redirect: (to) => ({ name: 'watchlists', query: to.query, hash: to.hash }),
      },
      {
        path: 'scheduled-reports',
        redirect: (to) => ({ name: 'scheduled-reports', query: to.query, hash: to.hash }),
      },
      {
        path: 'bulk-reports',
        redirect: (to) => ({ name: 'bulk-reports', query: to.query, hash: to.hash }),
      },
      {
        path: 'carriers/:dot',
        redirect: (to) => ({
          name: 'carrier-profile',
          params: { dot: to.params.dot },
          query: to.query,
          hash: to.hash,
        }),
      },
      {
        path: 'carriers/:dot/calculator',
        redirect: (to) => ({
          name: 'carrier-calculator',
          params: { dot: to.params.dot },
          query: to.query,
          hash: to.hash,
        }),
      },
      {
        // old segment `parity-audit` → new segment `parity`
        path: 'carriers/:dot/parity-audit',
        redirect: (to) => ({
          name: 'carrier-parity-audit',
          params: { dot: to.params.dot },
          query: to.query,
          hash: to.hash,
        }),
      },
      {
        path: 'vehicles/:vin',
        redirect: (to) => ({
          name: 'vehicle-history',
          params: { vin: to.params.vin },
          query: to.query,
          hash: to.hash,
        }),
      },
      {
        path: 'shippers/:name',
        redirect: (to) => ({
          name: 'shipper-search',
          params: { name: to.params.name },
          query: to.query,
          hash: to.hash,
        }),
      },

      // Compliance section (CAB data from PostgreSQL)
      {
        path: 'compliance',
        children: [
          {
            path: '',
            name: 'compliance-home',
            component: () => import('@views/compliance/ComplianceDashboardView.vue'),
            meta: { title: 'Compliance | ClaimShield Ops' },
          },
          {
            path: 'clients',
            name: 'compliance-clients',
            component: () => import('@views/compliance/ClientsView.vue'),
            meta: { title: 'Compliance Clients | ClaimShield Ops' },
          },
          {
            path: 'clients/:id',
            name: 'compliance-client-detail',
            component: () => import('@views/compliance/ClientDetailView.vue'),
            meta: { title: 'Client Details | ClaimShield Ops' },
          },
          {
            path: 'inspections',
            name: 'compliance-inspections',
            component: () => import('@views/compliance/InspectionsView.vue'),
            meta: { title: 'Inspections | ClaimShield Ops' },
          },
          {
            path: 'inspections/:id',
            name: 'compliance-inspection-detail',
            component: () => import('@views/compliance/InspectionDetailView.vue'),
            meta: { title: 'Inspection Details | ClaimShield Ops' },
          },
          {
            // §396.9(d)(3) 15-day certification chase list (F2) — consultant-facing
            path: 'overdue-certifications',
            name: 'compliance-overdue-certifications',
            component: () => import('@views/compliance/OverdueCertificationsView.vue'),
            meta: { title: 'Overdue Certifications | ClaimShield Ops' },
          },
          {
            path: 'vehicles',
            name: 'compliance-vehicles',
            component: () => import('@views/compliance/VehiclesView.vue'),
            meta: { title: 'Vehicles | ClaimShield Ops' },
          },
          {
            path: 'fleet/:clientId',
            name: 'compliance-client-fleet',
            component: () => import('@views/compliance/ClientFleetView.vue'),
            meta: { title: 'Fleet | ClaimShield Ops' },
          },
          {
            path: 'vehicles/:id',
            name: 'compliance-vehicle-detail',
            component: () => import('@views/compliance/VehicleDetailView.vue'),
            meta: { title: 'Vehicle Details | ClaimShield Ops' },
          },
          {
            path: 'violations',
            name: 'compliance-violations',
            component: () => import('@views/compliance/ViolationsView.vue'),
            meta: { title: 'Violations Analysis | ClaimShield Ops' },
          },
          {
            path: 'safety-scores',
            name: 'compliance-safety-scores',
            component: () => import('@views/compliance/SafetyScoresView.vue'),
            meta: { roles: ['staff', 'admin', 'developer'], title: 'Safety Scores | ClaimShield Ops' },
          },
          {
            path: 'monitoring',
            name: 'compliance-monitoring',
            component: () => import('@views/compliance/ComplianceMonitoringView.vue'),
            meta: { title: 'Compliance Monitoring | ClaimShield Ops' },
          },
          {
            // DataQ case queue + override drawer (Task 7.2)
            path: 'dataq',
            name: 'compliance-dataq',
            component: () => import('@views/compliance/DataqCasesView.vue'),
            meta: { roles: ['staff', 'admin', 'developer'], title: 'DataQ Challenges | ClaimShield Ops' },
          },
        ],
      },

      // Remedial Training section
      {
        path: 'remedial-training',
        children: [
          {
            path: '',
            name: 'remedial-home',
            component: () => import('@views/remedial/RemedialDashboardView.vue'),
            meta: { title: 'Remedial Training | ClaimShield Ops' },
          },
          {
            path: 'assignments',
            name: 'remedial-assignments',
            component: () => import('@views/remedial/AssignmentsView.vue'),
            meta: { title: 'Training Assignments | ClaimShield Ops' },
          },
          {
            path: 'courses',
            name: 'remedial-courses',
            component: () => import('@views/remedial/CourseCatalogView.vue'),
            meta: { title: 'Course Catalog | ClaimShield Ops' },
          },
          {
            path: 'unmatched-violations',
            name: 'remedial-unmatched',
            component: () => import('@views/remedial/UnmatchedViolationsView.vue'),
            meta: { title: 'Unmatched Violations | ClaimShield Ops' },
          },
        ],
      },

      // Consortium (D&A testing)
      {
        path: 'consortium',
        component: () => import('@views/consortium/ConsortiumLayout.vue'),
        meta: { title: 'Consortium | ClaimShield Ops' },
        children: [
          {
            path: '',
            redirect: '/consortium/selections',
          },
          {
            path: 'selections',
            name: 'consortium-selections',
            component: () => import('@views/consortium/MySelections.vue'),
            meta: { title: 'My Selections | ClaimShield Ops' },
          },
          {
            path: 'compliance',
            name: 'consortium-compliance',
            component: () => import('@views/consortium/ClientCompliance.vue'),
            meta: { title: 'Client Compliance | ClaimShield Ops' },
          },
          {
            path: 'order',
            name: 'consortium-order',
            component: () => import('@views/consortium/ManualTestEntry.vue'),
            meta: { title: 'Order Test | ClaimShield Ops' },
          },
          {
            path: 'clearinghouse',
            name: 'consortium-clearinghouse',
            component: () => import('@views/consortium/ClearinghouseWorklist.vue'),
            meta: { title: 'Clearinghouse Worklist | ClaimShield Ops' },
          },
          {
            path: 'driver/:employeeId/history',
            name: 'consortium-driver-history',
            component: () => import('@views/consortium/DriverHistory.vue'),
            meta: { title: 'Driver History | ClaimShield Ops' },
          },
          {
            path: 'admin',
            name: 'consortium-admin',
            component: () => import('@views/consortium/AdminDashboard.vue'),
            meta: { title: 'Consortium Admin | ClaimShield Ops', requiresAdmin: true },
          },
          {
            path: 'config',
            name: 'consortium-config',
            component: () => import('@views/consortium/AdminConfig.vue'),
            meta: { title: 'Consortium Config | ClaimShield Ops', requiresAdmin: true },
          },
        ],
      },

      // Training Library (staff + admin)
      {
        path: 'training-library',
        name: 'training-library',
        component: () => import('../views/TrainingLibraryView.vue'),
        meta: { title: 'Training Library | ClaimShield Ops', roles: ['staff', 'admin'] },
      },

      // Guides — consultant how-to library (staff + admin)
      {
        path: 'guides',
        name: 'guides',
        component: () => import('../views/guides/GuidesView.vue'),
        meta: { title: 'Guides | ClaimShield Ops', roles: ['staff', 'admin'] },
      },
      {
        // /guides/view/:slug (not /guides/:slug) so the SPA route never collides
        // with the static guide files served at /guides/<name>.html (CF Pages
        // strips .html → /guides/<name>, which would otherwise shadow this route
        // on direct-load / refresh).
        path: 'guides/view/:slug',
        name: 'guide-viewer',
        component: () => import('../views/guides/GuideViewerView.vue'),
        meta: { title: 'Guide | ClaimShield Ops', roles: ['staff', 'admin'] },
      },

      // Held screening requests — public MVR/PSP form submissions from an email not on
      // file for the client wait here for the consultant (security fix 2026-10-28).
      // Linked from the consultant's task (link_url '/screening-holds').
      {
        path: 'screening-holds',
        name: 'screening-holds',
        component: () => import('../views/ScreeningHoldsView.vue'),
        meta: { title: 'Held screening requests | ClaimShield Ops', roles: ['staff', 'admin'] },
      },

      // Training Review — JJK course replacement approval queue (admin only)
      {
        path: 'training-review',
        name: 'training-review',
        component: () => import('../views/TrainingReviewView.vue'),
        meta: { title: 'Training Review | ClaimShield Ops', roles: ['admin'] },
      },

      // Course Review — the human review loop's companion console (Variant A):
      // the course opens in its 1:1 Moodle popup, the console flags on the
      // frozen frame. Staff + admin (the reviewer roster is a server-side
      // setting). API: /api/v1/course-workflow/{run}/reviews · docs:
      // knowledge/academy/course-studio/specs/2026-09-15-human-review-loop-design.md
      {
        path: 'course-review/:runId',
        name: 'course-review',
        component: () => import('../views/CourseReviewView.vue'),
        props: true,
        meta: { title: 'Course Review | ClaimShield Ops', roles: ['staff', 'admin', 'developer'] },
      },

      // Course Runs — the operator console for the agentic course-production
      // pipeline: list/find runs, inspect QA + logs, start/pause/resume/cancel.
      // Same component for the list and the detail-open-by-id shape (a failure
      // notice links straight to /course-runs/<id>), same audience as Course
      // Review above (list_runs and get_run are both CurrentStaffOrDeveloper;
      // operator actions are gated client-side by the /access can_operate flag
      // and server-side by CurrentCourseOperator either way).
      // API: /api/v1/course-workflow · backend: app/api/v1/course_build.py
      {
        path: 'course-runs',
        name: 'course-runs',
        component: () => import('../views/CourseRunsView.vue'),
        meta: { title: 'Course Runs | ClaimShield Ops', roles: ['staff', 'admin', 'developer'] },
      },
      {
        path: 'course-runs/:runId',
        name: 'course-runs-detail',
        component: () => import('../views/CourseRunsView.vue'),
        props: true,
        meta: { title: 'Course Runs | ClaimShield Ops', roles: ['staff', 'admin', 'developer'] },
      },

      // Driver File Review — flagged client-uploaded driver files awaiting routing
      {
        path: 'transportation/driver-file-review',
        name: 'driver-file-review',
        component: () => import('@views/DriverFileReviewView.vue'),
        meta: { title: 'Driver File Review | ClaimShield Ops' },
      },

      // File Review Finding — the target of Command Center file-review cards:
      // the AI file-review finding + document preview for one finding.
      {
        path: 'transportation/file-review/:findingId',
        name: 'file-review-finding',
        component: () => import('@views/FileReviewFindingView.vue'),
        // developer since 2026-10-08 (Ahmed): the review queue's owner closes
        // findings here. The page's reads already admit the role; the resolve
        // panel renders only for the developers the server names.
        meta: { title: 'File Review | ClaimShield Ops', roles: ['staff', 'admin', 'developer'] },
      },
      {
        // Broker drift leads (Phase A Task 7 Step 7). staff+admin because
        // Prosser works this card and is `staff`; the API behind it is
        // CurrentStaff, and the nav entry in useOpsNav sits under Sales.
        // All three layers must agree or the page is reachable-but-hidden
        // or visible-but-403.
        path: 'sales/drift-leads',
        name: 'drift-leads',
        component: () => import('@views/DriftLeadsView.vue'),
        meta: { title: 'Broker Drift Leads | ClaimShield Ops', roles: ['staff', 'admin'] },
      },
      {
        // Carrier disputes (broker-platform spec 2026-10-02 §6). staff+admin to
        // match the API (CurrentStaff) and the Sales nav entry in useOpsNav.
        path: 'sales/carrier-disputes',
        name: 'carrier-disputes',
        component: () => import('@views/broker/CarrierDisputesView.vue'),
        meta: { title: 'Carrier Disputes | ClaimShield Ops', roles: ['staff', 'admin'] },
      },
      {
        path: 'sales/carrier-disputes/:id',
        name: 'carrier-dispute',
        component: () => import('@views/broker/CarrierDisputeCaseView.vue'),
        meta: { title: 'Carrier Dispute | ClaimShield Ops', roles: ['staff', 'admin'] },
      },
      {
        path: 'file-review',
        name: 'file-review-queue',
        component: () => import('@views/FileReviewQueueView.vue'),
        meta: { title: 'AI Review Queue | ClaimShield Ops', roles: ['admin', 'developer'] },
      },
      {
        path: 'file-review/review/:id',
        name: 'file-review-mode',
        component: () => import('@views/FileReviewMode.vue'),
        meta: { title: 'AI Review | ClaimShield Ops', roles: ['admin', 'developer'] },
      },

      // Shared tools
      {
        path: 'reports',
        name: 'ops-reports',
        component: () => import('@views/ReportsView.vue'),
        meta: { title: 'Reports | ClaimShield Ops' },
      },
      {
        path: 'profile',
        name: 'ops-profile',
        component: () => import('@views/ProfileView.vue'),
        // Explicit roles (vs inheriting the parent's staff/admin) so the
        // restricted developer and legal roles can manage their own account
        // too. The page only calls PATCH /v1/users/me (any active user).
        meta: { title: 'Profile | ClaimShield Ops', roles: ['staff', 'admin', 'developer', 'legal'] },
      },
      {
        path: 'notification-preferences',
        name: 'notification-preferences',
        component: () => import('@views/NotificationPreferencesView.vue'),
        meta: { title: 'Notification Preferences | ClaimShield Ops' },
      },

      // The legacy ops driver-application form was DELETED 2026-08-24 (Ahmed).
      // Its own comment called it development/testing, nothing linked to it,
      // and it had drifted 3,752 lines away from the canonical form at
      // apply.claimshieldpro.com. The public /forms/apply route already
      // redirects there (retired 2026-06-16); this was the last thing loading
      // the old component. Spec: knowledge/ops-portal/specs/retire-ops-apply-form.md

      // Company presentations (fullscreen mode)
      {
        path: 'presentations/q1-2026-kickoff',
        name: 'presentation-q1-2026',
        component: () => import('@views/presentations/Q1KickoffView.vue'),
        meta: {
          title: 'ClaimShield 2.026 | ClaimShield Ops',
          fullscreen: true,
        },
      },

      {
        path: 'fleet/vehicles/review',
        name: 'fleet-vehicle-review',
        component: () => import('@views/fleet/VehicleReviewView.vue'),
        meta: { title: 'Vehicle File Review | ClaimShield Ops', roles: ['staff', 'admin'] },
      },
      {
        path: 'fleet/vehicles/:id/audit',
        name: 'fleet-vehicle-audit',
        component: () => import('@views/fleet/VehicleAuditView.vue'),
        meta: { title: 'Vehicle Audit | ClaimShield Ops', roles: ['staff', 'admin'] },
      },
      // Fleet vehicle detail (fleet-management v2 — owns /fleet/vehicles/:id)
      // Note: /compliance/vehicles/:id remains for FMCSA-data vehicles (different table)
      {
        path: 'fleet/vehicles/:id',
        name: 'fleet-vehicle-detail',
        component: () => import('@views/fleet/FleetVehicleDetailView.vue'),
        meta: { requiresAuth: true, roles: ['staff', 'admin'], title: 'Vehicle | ClaimShield Ops' },
      },
      // Repairs — the defect-to-repair loop (ops port), one carrier at a time
      {
        path: 'fleet/repairs',
        name: 'fleet-repairs',
        component: () => import('@views/fleet/RepairsView.vue'),
        meta: { requiresAuth: true, roles: ['staff', 'admin'], title: 'Repairs | ClaimShield Ops' },
      },
      // Invoice review queue (P4 ops port) — one carrier at a time, picked in-view
      {
        path: 'fleet/roadside-review',
        name: 'fleet-roadside-review',
        component: () => import('@views/fleet/RoadsideReviewView.vue'),
        meta: { requiresAuth: true, roles: ['staff', 'admin'], title: 'Roadside Review | ClaimShield Ops' },
      },
      {
        path: 'fleet/portal-reports',
        name: 'fleet-portal-reports',
        component: () => import('@views/fleet/PortalReportUploadView.vue'),
        meta: { requiresAuth: true, roles: ['staff', 'admin'], title: 'FMCSA Portal Reports | ClaimShield Ops' },
      },
      {
        path: 'fleet/invoice-review',
        name: 'fleet-invoice-review',
        component: () => import('@views/fleet/InvoiceReviewView.vue'),
        meta: { requiresAuth: true, roles: ['staff', 'admin'], title: 'Invoice Review | ClaimShield Ops' },
      },
      // Placeholder: DVIR creation view (separate scope — flagged as TODO)
      {
        path: 'fleet/vehicles/:id/dvir/new',
        name: 'fleet-vehicle-dvir-new',
        component: () => import('@views/fleet/FleetVehicleDetailView.vue'),
        meta: { requiresAuth: true, roles: ['staff', 'admin'], title: 'New DVIR | ClaimShield Ops' },
      },

      // E-Signature (native Zoho Sign replacement) — staff-gated admin views
      {
        path: 'esign',
        name: 'esign-list',
        component: () => import('@views/esign/EnvelopeListView.vue'),
        meta: { roles: ['staff', 'admin', 'developer'], title: 'Document Signing | ClaimShield Ops' },
      },
      {
        path: 'esign/new',
        name: 'esign-create',
        component: () => import('@views/esign/EnvelopeCreateView.vue'),
        meta: { roles: ['staff', 'admin', 'developer'], title: 'New Envelope | ClaimShield Ops' },
      },
      // Templates — declared BEFORE the dynamic esign/:id so the static
      // `templates` / `from-template` paths win the match.
      {
        path: 'esign/templates',
        name: 'esign-template-list',
        component: () => import('@views/esign/TemplateListView.vue'),
        meta: { roles: ['staff', 'admin', 'developer'], title: 'Signing Templates | ClaimShield Ops' },
      },
      {
        path: 'esign/templates/new',
        name: 'esign-template-new',
        component: () => import('@views/esign/TemplateEditView.vue'),
        meta: { roles: ['staff', 'admin', 'developer'], title: 'New Template | ClaimShield Ops' },
      },
      {
        path: 'esign/templates/:id/use',
        name: 'esign-template-use',
        component: () => import('@views/esign/EnvelopeFromTemplateView.vue'),
        meta: { roles: ['staff', 'admin', 'developer'], title: 'Use Template | ClaimShield Ops' },
      },
      {
        // Edit an existing template's field placements (field-editor Phase 2).
        // Declared with the other static `templates/...` paths, before esign/:id.
        path: 'esign/templates/:id/edit',
        name: 'esign-template-edit',
        component: () => import('@views/esign/TemplateEditPlacementsView.vue'),
        props: true,
        meta: { roles: ['staff', 'admin', 'developer'], title: 'Edit Template Placements | ClaimShield Ops' },
      },
      {
        path: 'esign/from-template',
        name: 'esign-from-template',
        component: () => import('@views/esign/EnvelopeFromTemplateView.vue'),
        meta: { roles: ['staff', 'admin', 'developer'], title: 'New from Template | ClaimShield Ops' },
      },
      {
        path: 'esign/:id',
        name: 'esign-detail',
        component: () => import('@views/esign/EnvelopeDetailView.vue'),
        meta: { roles: ['staff', 'admin', 'developer'], title: 'Envelope | ClaimShield Ops' },
      },
      {
        path: 'esign/:id/preview',
        name: 'esign-preview',
        component: () => import('@views/esign/EnvelopePreviewView.vue'),
        meta: { roles: ['staff', 'admin', 'developer'], title: 'Preview Signing | ClaimShield Ops' },
      },

      // Robley nurture campaigns
      {
        path: 'robley/nurture',
        name: 'nurture-campaigns',
        component: () => import('@views/robley/NurtureCampaignsView.vue'),
        meta: { title: 'Nurture Campaigns | ClaimShield Ops' },
      },
      {
        // Must be before :id so the literal 'new' isn't captured as a campaign id
        path: 'robley/nurture/new',
        name: 'nurture-campaign-new',
        component: () => import('@views/robley/NurtureCampaignDetail.vue'),
        meta: { title: 'New Nurture Campaign | ClaimShield Ops' },
      },
      {
        // Calendar preview — specific literal before parametric :id
        path: 'robley/nurture/calendar',
        name: 'nurture-calendar',
        component: () => import('@views/robley/NurtureCalendarView.vue'),
        meta: { title: 'Nurture Calendar | ClaimShield Ops' },
      },
      {
        // Template library — specific literal before parametric :id (UX-12)
        path: 'robley/nurture/templates',
        name: 'nurture-templates',
        component: () => import('@views/robley/NurtureTemplatesView.vue'),
        meta: { title: 'Nurture Templates | ClaimShield Ops' },
      },
      {
        // Hot Leads sales dashboard — specific literal before parametric :id (UX-17)
        path: 'robley/nurture/hot-leads',
        name: 'nurture-hot-leads',
        component: () => import('@views/robley/NurtureHotLeadsView.vue'),
        meta: { title: 'Hot Leads | ClaimShield Ops' },
      },
      {
        // Notes feed audit log — specific literal before parametric :id (UX-30)
        path: 'robley/nurture/notes-feed',
        name: 'nurture-notes-feed',
        component: () => import('@views/robley/NurtureNotesFeedView.vue'),
        meta: { title: 'Notes Feed | ClaimShield Ops' },
      },
      {
        path: 'robley/nurture/:id',
        name: 'nurture-campaign-detail',
        component: () => import('@views/robley/NurtureCampaignDetail.vue'),
        meta: { title: 'Nurture Campaign | ClaimShield Ops' },
      },
      {
        path: 'robley/nurture/:id/:tab(setup|touchpoints|enrollments)',
        name: 'nurture-campaign-tab',
        component: () => import('@views/robley/NurtureCampaignDetail.vue'),
        meta: { title: 'Nurture Campaign | ClaimShield Ops' },
      },

      // Robley nurture relationship board (Subsystem A — cold/warm/hot)
      // Spec: knowledge/robley-integration/specs/2026-05-28-subsystem-a-prospecting-nurture-design.md
      {
        path: 'robley/nurture-board',
        name: 'robley-nurture-board',
        component: () => import('@views/robley/NurtureBoardView.vue'),
        meta: { title: 'Nurture Board | ClaimShield Ops' },
      },

      // Robley submission pickups
      {
        path: 'robley/pickups',
        name: 'robley-pickups',
        component: () => import('@views/robley/SubmissionPickupsView.vue'),
        meta: { title: 'Submission Pickups | ClaimShield Ops' },
      },
      // Robley Action Center — funnel strip + stage rail + per-stage queues
      // + outreach call queue. Absorbs the Subsystem D task dashboard below.
      // Spec: knowledge/robley-integration/specs/2026-07-09-robley-action-center-design.md
      {
        path: 'robley/action-center',
        name: 'robley-action-center',
        component: () => import('@views/robley/action-center/RobleyActionCenterView.vue'),
        meta: { title: 'Action Center | ClaimShield Ops' },
      },
      // Robley Outreach Cockpit — the Outreach stage's deep-work room.
      // Composes the same shared subcomponents as CallQueuePane.vue (never
      // forks them). Also registered in the crm-tab hash router + Zoho tab.
      // Spec: knowledge/robley-integration/specs/2026-07-09-outreach-cockpit-design.md
      {
        path: 'robley/outreach',
        name: 'robley-outreach-cockpit',
        component: () => import('@views/robley/action-center/OutreachCockpitView.vue'),
        meta: { title: 'Outreach Cockpit | ClaimShield Ops' },
      },
      // Robley agent task dashboard (Subsystem D) — absorbed into Action Center.
      // Spec: knowledge/robley-integration/specs/2026-05-28-subsystem-d-task-dashboard-design.md
      {
        path: 'robley/tasks',
        name: 'robley-tasks',
        redirect: { name: 'robley-action-center' },
      },

      // Consultant feedback — requests list + detail
      {
        path: 'support/requests',
        name: 'support-requests',
        component: () => import('@views/support/RequestsView.vue'),
        meta: { title: 'Requests | ClaimShield Ops' },
      },
      // Client messages — unanswered-thread queue + portal presence (Phase 2C).
      // Beside Requests, not merged into it: a request is a ticket with a
      // lifecycle, this is a carrier waiting on a human.
      {
        path: 'support/messages',
        name: 'client-message-queue',
        component: () => import('@views/support/MessageQueueView.vue'),
        meta: { title: 'Client Messages | ClaimShield Ops' },
      },

      // Legal department (S22). The parent record carries
      // meta.roles = ['admin','legal'], which OVERRIDES this layout route's
      // ['staff','admin'] through vue-router's per-key meta merge — the same
      // mechanism the AI Review Queue uses for ['admin','developer'].
      ...legalRoutes,
    ],
  },


  // Legacy public application form — RETIRED 2026-06-16. The DriverApplicationForm
  // component this route used to serve never persisted uploaded documents: all
  // four of its file-upload handlers (license/ELDT/W-2/medical cert) were stubs
  // that stored the filename and uploaded nothing, so every driver document
  // attached via this path was silently dropped (confirmed on the Antwon Polk /
  // Gratitude Services application — zero rows in the documents table). The
  // canonical, working public form is the marketing app at
  // apply.claimshieldpro.com/:dotNumber/:formType, which captures license
  // front/back + medical cert correctly. Bounce old DOT-based links there
  // instead of serving the broken form. (New share/QR links already point at
  // apply.claimshieldpro.com.) Mirrors the apply-redirect rescue below.
  {
    path: '/forms/apply/:dotNumber/:formType',
    name: 'public-driver-application',
    beforeEnter: (to) => {
      const { dotNumber, formType } = to.params
      // Preserve the query string — draft-reminder emails linked here with
      // ?resume=<token>; dropping it restarted drivers at step 1 (Antwon
      // Polk / Gratitude Services, 2026-07-24).
      window.location.replace(
        `https://apply.claimshieldpro.com/${dotNumber}/${formType}${window.location.search}`,
      )
    },
    component: NotFoundView,
    meta: {
      requiresAuth: false,
      layout: 'fullscreen',
      title: 'Redirecting…',
    },
  },

  // Authorization form signing (public, token-based)
  {
    path: '/sign/:accessToken',
    name: 'authorization-signing',
    component: () => import('@views/sign/AuthorizationSigningView.vue'),
    meta: {
      requiresAuth: false,
      layout: 'fullscreen',
      title: 'Authorization Forms | ClaimShield',
    },
  },

  // Onboarding packet signing (public, token-based, post-hire)
  {
    path: '/onboard/:accessToken',
    name: 'onboarding-packet',
    component: () => import('@views/onboard/OnboardingPacketView.vue'),
    meta: {
      requiresAuth: false,
      layout: 'fullscreen',
      title: 'Onboarding Documents | ClaimShield',
    },
  },

  // VOE employer self-service verification (public, token-based)
  {
    path: '/voe-verify/:token',
    name: 'voe-verification',
    component: () => import('@views/transportation/VOEVerificationForm.vue'),
    meta: {
      requiresAuth: false,
      layout: 'fullscreen',
      title: 'Employment Verification | ClaimShield',
    },
  },

  // Quote presentation to client (public, token-based, Robley-branded)
  // Spec: knowledge/robley-integration/specs/2026-05-31-quote-presentation-design.md §5
  {
    path: '/quote/:token',
    name: 'client-quote',
    component: () => import('@views/quote/ClientQuoteView.vue'),
    meta: {
      requiresAuth: false,
      layout: 'fullscreen',
      title: 'Your Quotes | Robley',
    },
  },

  // CRM bridge session handoff (Atlas wave 5 X-H): the popup the Sentinel
  // sign-in shell opens from inside a Zoho CRM tab. Top-level (no OpsLayout
  // chrome); the guard signs the consultant in first if needed and returns
  // here with the nonce intact. Logic + checks: lib/bridgeHandoff.ts.
  {
    path: '/bridge-handoff',
    name: 'bridge-handoff',
    component: () => import('@views/BridgeHandoffView.vue'),
    meta: { requiresAuth: true, roles: ['staff', 'admin'], title: 'Signing in | ClaimShield Ops' },
  },

  // Logout redirect
  {
    path: '/logout',
    name: 'logout',
    redirect: '/login',
  },

  // Wrong-subdomain rescue: drivers occasionally hit
  // `ops.claimshieldpro.com/apply/<dot>/<formType>` (typo, stale link, or a
  // share that dropped the subdomain). The real apply site is on
  // `apply.claimshieldpro.com/<dot>/<formType>`. Bounce them there instead of
  // landing on a 404 inside the staff portal.
  {
    path: '/apply/:dotNumber(\\d+)/:formType',
    name: 'apply-redirect',
    beforeEnter: (to) => {
      const { dotNumber, formType } = to.params
      // Preserve the query string — draft-reminder emails linked here with
      // ?resume=<token>; dropping it restarted drivers at step 1 (Antwon
      // Polk / Gratitude Services, 2026-07-24).
      window.location.replace(
        `https://apply.claimshieldpro.com/${dotNumber}/${formType}${window.location.search}`,
      )
    },
    component: NotFoundView,
    meta: { requiresAuth: false, title: 'Redirecting…' },
  },

  // 404 catch-all
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: NotFoundView,
    meta: { requiresAuth: false, title: 'Not Found | ClaimShield Ops' },
  },
]

export const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(_to, _from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
    return { top: 0 }
  },
})

// Setup navigation guards
setupGuards(router)

// The flag is a session capability. The backend checks it again on every desk request.
export function hasAuditDeskAccess(user: Pick<User, 'role' | 'audit_desk'> | null): boolean {
  return user?.role === 'admin' || (user?.role === 'staff' && user.audit_desk === true)
}
router.beforeEach((to) => {
  if (!to.meta.requiresAuditDesk) return true
  return hasAuditDeskAccess(useAuthStore().user) ? true : { name: 'dashboard' }
})

// Embed-mode propagation: when the user opens an iframe URL with `?embed=1`
// (set by the Zoho CRM proposal-dashboard widget), every subsequent in-app
// navigation must keep `embed=1` so OpsLayout stays in chrome-less mode.
// Without this, clicking "Edit" on a proposal navigates to /sales/proposals/:id/edit
// without the embed flag and the sidebar reappears inside the CRM iframe.
router.beforeEach((to, from) => {
  if (to.query.embed === '1') return true
  if (from.query.embed !== '1') return true
  return { ...to, query: { ...to.query, embed: '1' } }
})

// Stale-chunk reload backstop for failures that never reach router.onError —
// a lazy import fired from a non-route component, or a raw <script> load.
//
// The router-level handler is registered by setupGuards() above, which routes to
// the ONE shared implementation. ops previously carried its OWN copy here
// (isStaleChunkError + reloadOnStaleChunk, a 10s sessionStorage debounce and a
// bare window.location.reload()), registered on router.onError ALONGSIDE the
// shared one. vue-router invokes every registered onError handler, so both fired
// and raced: the local copy reloaded the SAME url with no cache-busting marker,
// which is precisely the reload that cannot escape a poisoned cache entry — it
// could win the race and defeat the fix. Its sessionStorage.setItem was also
// unguarded, the same throw-on-blocked-storage bug fixed in the shared module.
// Removed 2026-08-14 (adversarial review); only the window-level backstop
// remains, now delegating to the shared implementation.
window.addEventListener('unhandledrejection', (event) => {
  const reason = event.reason
  const message = reason instanceof Error ? reason.message : String(reason ?? '')
  if (isChunkLoadErrorMessage(message)) attemptChunkReload()
})
